import React, { useState } from 'react';
import {
  Container, Typography, Box, Paper, TextField, Button,
  Alert, Breadcrumbs, Link as MuiLink, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions
} from '@mui/material';
import { Link } from 'react-router-dom';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import axios from 'axios';
import { getApiUrl } from '../utils/runtimeConfig';

const ExcluirConta: React.FC = () => {
  const [docOrEmail, setDocOrEmail] = useState('');
  const [motivo, setMotivo] = useState('');
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const handleSubmit = async () => {
    setConfirmDialogOpen(false);
    setErrorMsg(null);
    setSuccessMsg(null);

    if (!docOrEmail.trim()) {
      setErrorMsg('Por favor, informe seu CPF, CNPJ ou E-mail cadastrado.');
      return;
    }

    try {
      setLoading(true);
      const isEmail = docOrEmail.includes('@');
      const payload: any = {
        motivo: motivo.trim() || 'Solicitação direta pelo usuário',
      };
      if (isEmail) {
        payload.email = docOrEmail.trim();
      } else {
        payload.cpf_cnpj = docOrEmail.trim();
      }

      const apiUrl = getApiUrl() || process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const response = await axios.post(`${apiUrl}/users/solicitar-exclusao`, payload);

      if (response.data?.success) {
        setSuccessMsg(
          response.data.message ||
          'Sua solicitação de exclusão foi processada com sucesso. Seus dados e seu perfil empresarial foram desativados e removidos da plataforma.'
        );
        setDocOrEmail('');
        setMotivo('');
      } else {
        setErrorMsg(response.data?.message || 'Não foi possível processar a solicitação.');
      }
    } catch (err: any) {
      setErrorMsg(err.response?.data?.message || 'Erro ao conectar ao servidor. Tente novamente mais tarde.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '80vh', py: 4, backgroundColor: '#f8f9fa' }}>
      <Container maxWidth="md">
        <Breadcrumbs sx={{ mb: 3 }}>
          <MuiLink component={Link} to="/" color="inherit" underline="hover">
            Início
          </MuiLink>
          <Typography color="text.primary">Exclusão de Conta e Dados</Typography>
        </Breadcrumbs>

        <Paper elevation={0} sx={{ p: { xs: 3, md: 5 }, borderRadius: 3, border: '1px solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '12px',
                bgcolor: 'rgba(194, 53, 53, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#C23535',
              }}
            >
              <DeleteForeverIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h4" component="h1" fontWeight={700} color="#202529">
                Exclusão de Conta e Dados Pessoais
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Diretrizes de Privacidade Apple App Store, Google Play Store e LGPD (Lei nº 13.709/2018)
              </Typography>
            </Box>
          </Box>

          <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 3, borderRadius: 2 }}>
            A exclusão da conta é <strong>permanente e irreversível</strong>. Ao confirmar, seu acesso ao IDECONECTA será revogado e os dados da sua empresa deixarão de ser exibidos no catálogo de classificados.
          </Alert>

          <Typography paragraph sx={{ lineHeight: 1.8 }}>
            Em respeito ao seu direito à privacidade e às políticas das lojas oficiais de aplicativos (App Store e Play Store), qualquer usuário ou empresário cadastrado na plataforma IDECONECTA pode solicitar a exclusão de sua conta e dos dados vinculados.
          </Typography>

          <Typography variant="h6" fontWeight={700} sx={{ mt: 3, mb: 1, color: '#202529' }}>
            Quais dados são excluídos?
          </Typography>
          <ul>
            <li>Dados pessoais de cadastro (nome, CPF, telefone e e-mail);</li>
            <li>Cadastro da empresa vinculada no catálogo público;</li>
            <li>Histórico de mensagens e comunicações internas;</li>
            <li>Tokens de autenticação e sessões ativas nos aplicativos móveis.</li>
          </ul>

          <Typography variant="h6" fontWeight={700} sx={{ mt: 4, mb: 2, color: '#202529' }}>
            Formulário de Solicitação
          </Typography>

          {successMsg && (
            <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
              {successMsg}
            </Alert>
          )}

          {errorMsg && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              {errorMsg}
            </Alert>
          )}

          <Box component="form" onSubmit={(e) => { e.preventDefault(); setConfirmDialogOpen(true); }}>
            <TextField
              fullWidth
              label="CPF, CNPJ ou E-mail cadastrado"
              value={docOrEmail}
              onChange={(e) => setDocOrEmail(e.target.value)}
              placeholder="Digite seu CPF, CNPJ ou e-mail"
              margin="normal"
              required
              helperText="Identifique o cadastro que você deseja excluir da plataforma."
            />

            <TextField
              fullWidth
              label="Motivo da exclusão (opcional)"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Conte-nos o motivo para nos ajudar a melhorar (opcional)"
              margin="normal"
              multiline
              rows={3}
            />

            <Button
              type="submit"
              variant="contained"
              color="error"
              size="large"
              disabled={loading || !docOrEmail.trim()}
              sx={{ mt: 2, px: 4, py: 1.2, fontWeight: 700 }}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <DeleteForeverIcon />}
            >
              {loading ? 'Processando exclusão...' : 'Solicitar Exclusão Definitiva'}
            </Button>
          </Box>
        </Paper>
      </Container>

      {/* Modal de confirmação */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        aria-labelledby="confirm-delete-title"
      >
        <DialogTitle id="confirm-delete-title" sx={{ fontWeight: 700, color: '#C23535' }}>
          Confirmar Exclusão de Conta?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Você está prestes a solicitar a exclusão de todos os dados associados a <strong>{docOrEmail}</strong>.
            Esta ação não poderá ser desfeita. Tem certeza de que deseja prosseguir?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setConfirmDialogOpen(false)} color="inherit">
            Cancelar
          </Button>
          <Button onClick={handleSubmit} color="error" variant="contained">
            Sim, excluir definitivamente
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExcluirConta;
